<template>
  <div class="select-task-container">
    <div class="task-header" @click="toggleCollapse">
      <h3 class="task-title">בחירת משימה</h3>
      <span class="triangle" :class="{ 'open': isOpen }">▶</span>
    </div>

    <div v-if="isOpen" class="task-content">
      <!-- Checkbox for finishing current task -->
      <div v-if="alertId || hasActiveTask" class="task-options">
        <div class="finish-task-checkbox">
          <label>
            <input
              type="checkbox"
              v-model="finishCurrentTask"
              @change="handleFinishTaskChange"
            />
            <span>סיימתי את המשימה</span>
          </label>
        </div>

        <div class="want-different-task-checkbox">
          <label>
            <input
              type="checkbox"
              v-model="wantDifferentTask"
              @change="handleWantDifferentTaskChange"
            />
            <span>רוצה משימה אחרת</span>
          </label>
        </div>
      </div>

    <div class="task-buttons">
      <button
        v-for="task in tasks"
        :key="task.id"
        class="task-button"
        :class="{
          'active': selectedTask === task.id,
          'disabled': (alertId || hasActiveTask) && !finishCurrentTask && !wantDifferentTask
        }"
        @click="selectTask(task)"
        :disabled="(alertId || hasActiveTask) && !finishCurrentTask && !wantDifferentTask"
      >
        {{ task.name }}
      </button>
      </div>
      <div v-if="selectedTask" class="selected-task-info">
        <p>משימה נבחרת: <strong>{{ getTaskName(selectedTask) }}</strong></p>
      </div>
      <div v-if="loading" class="loading">טוען...</div>
      <div v-if="message" class="message" :class="messageType">
        {{ message }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SelectTask',
  props: {
    alertId: {
      type: Number,
      default: null
    },
    childTaskComplete: {
      type: Boolean,
      default: false
    },
    hasActiveTask: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isOpen: false,
      selectedTask: null,
      loading: false,
      message: '',
      messageType: '',
      finishCurrentTask: false,
      wantDifferentTask: false,
      tasks: [
        {
          id: 'designated_position',
          name: 'משרה ייעודית',
          handler: 'DesignatedPositionHandler'
        },
        {
          id: 'blue_bubble',
          name: 'בועה כחולה',
          handler: 'BlueBubbleHandler'
        },
        {
          id: 'yellow_bubble',
          name: 'בועה צהובה',
          handler: 'YellowBubbleHandler'
        },
        {
          id: 'purple_bubble',
          name: 'בועה סגולה',
          handler: 'PurpleBubbleHandler'
        },
        {
          id: 'system_strong_bubble',
          name: 'בועה סטרונג מערכת',
          handler: 'SystemStrongBubbleHandler'
        },
        {
          id: 'intake_review_bubble',
          name: 'בועה כתומה',
          handler: 'IntakeReviewBubbleHandler'
        },
        {
          id: 'morning_emails',
          name: 'מיילים בוקר',
          isStatic: true
        },
        {
          id: 'noon_emails',
          name: 'מיילים צהריים',
          isStatic: true
        },
        {
          id: 'evening_emails',
          name: 'מיילים ערב',
          isStatic: true
        },
        {
          id: 'recruit_employers',
          name: 'גיוס מעסיקים חדשים',
          isStatic: true
        },
        {
          id: 'collections',
          name: 'גבייה',
          isStatic: true
        },
        {
          id: 'project_1000',
          name: 'פרויקט ה-1000',
          isStatic: true
        },
        {
          id: 'lead_board_tracking',
          name: 'מעקב לוח לידים',
          isStatic: true
        },
        {
          id: 'general_follow_up',
          name: 'פולואפ כללי',
          handler: 'GeneralFollowUpHandler'
        },
        //      {
        //        id: 'ignore_follow_up',
        //        name: 'פולואפ התעלמות',
        //        handler: 'IgnoreFollowUpHandler'
        //     },
        {
          id: 'late_follow_up',
          name: 'פולואפ מאוחר',
          handler: 'LateFollowUpHandler'
        },
        {
          id: 'advanced_follow_up',
          name: 'פולואפ מתקדם',
          handler: 'AdvancedFollowUpHandler'
        },
        {
          id: 'random_submissions_feed',
          name: 'פיד הגשות רנדומלי',
          handler: 'RandomSubmissionsFeedHandler'
        }
      ]
    }
  },
  methods: {
    toggleCollapse () {
      this.isOpen = !this.isOpen
    },

    handleFinishTaskChange () {
      if (this.finishCurrentTask) {
        // Uncheck the other checkbox (radio-like behavior)
        this.wantDifferentTask = false
        // Execute finish logic similar to FeedAlert's finish method
        this.finishTask()
      }
    },

    handleWantDifferentTaskChange () {
      if (this.wantDifferentTask) {
        // Uncheck the other checkbox (radio-like behavior)
        this.finishCurrentTask = false

        // Reset the task timer immediately when user wants a different task
        this.$emit('want-different-task')

        // Only open timing modal if there is an alert
        if (this.alertId) {
          this.openTimingModal()
        }
      }
    },

    async finishTask () {
      if (!this.alertId) return

      try {
        // Clear designated position state when finishing task
        localStorage.removeItem('designated_position_state')
        console.log('[SelectTask] Cleared designated_position_state on task finish')

        // Mark alert as done with duration (same logic as FeedAlert finish button)
        await this.$network_utils.updateAlert({
          id: this.alertId,
          status: 'done'
        })

        // Emit event to parent to show "waiting for task selection" message
        this.$emit('task-finished')
      } catch (error) {
        console.error('SelectTask: Error finishing task:', error)
        this.message = 'שגיאה בסיום המשימה: ' + (error.message || 'שגיאה לא ידועה')
        this.messageType = 'error'
        setTimeout(() => {
          this.message = ''
        }, 3000)
      }
    },

    openTimingModal () {
      // Use Vuex to create modal
      this.$store.commit('modals/create', {
        designator: 'alerts-timing-modal',
        component_name: 'FeedAlertEditTiming',
        modal_title: 'Alert\'s Timing',
        props: {
          alert: { id: this.alertId },
          isSnoozeAll: true,
          onSubmit: (modalId) => {
            // After modal is confirmed, emit event to parent
            this.$emit('timing-updated')
            this.$store.commit('modals/removeModalById', modalId)
          }
        }
      })
    },

    async selectTask (task) {
      // Check if user needs to check one of the options first
      if ((this.alertId || this.hasActiveTask) && !this.finishCurrentTask && !this.wantDifferentTask) {
        this.message = 'יש לבחור אחת מהאפשרויות לפני בחירת משימה חדשה'
        this.messageType = 'error'
        setTimeout(() => {
          this.message = ''
        }, 3000)
        return
      }

      this.selectedTask = task.id
      this.loading = true
      this.message = ''

      try {
        this.message = `משימה "${task.name}" נבחרה`
        this.messageType = 'success'

        // Reset checkboxes
        this.finishCurrentTask = false
        this.wantDifferentTask = false

        // Clear designated position localStorage when selecting "משרה ייעודית" task
        // This ensures the component starts fresh and doesn't skip screens
        if (task.id === 'designated_position') {
          localStorage.removeItem('designated_position_state')
          console.log('[SelectTask] Cleared designated_position_state for fresh start')
        }

        // Emit event to parent component with task info
        // Parent will call getAlert with manual_task_name (or show StaticTasks for static tasks)
        this.$emit('task-selected', {
          taskId: task.id,
          taskName: task.name,
          handler: task.handler,
          isStatic: task.isStatic || false
        })

        // Auto-close the component after task selection
        this.isOpen = false
      } catch (error) {
        console.error('SelectTask: Error selecting task:', error)
        this.message = 'שגיאה בבחירת המשימה: ' + (error.message || 'שגיאה לא ידועה')
        this.messageType = 'error'
      } finally {
        this.loading = false
      }
    },

    getTaskName (taskId) {
      const task = this.tasks.find(t => t.id === taskId)
      return task ? task.name : ''
    }
  }
}
</script>

<style scoped>
/* FollowUp-inspired styling */
.select-task-container {
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  overflow: hidden;
}

.task-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0.5rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: opacity 0.2s ease;
}

.task-header:hover {
  opacity: 0.9;
}

.task-title {
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.triangle {
  font-size: 0.9rem;
  color: #ffffff;
  transition: transform 0.3s ease;
  display: inline-block;
  margin-left: 8px;
}

.triangle.open {
  transform: rotate(90deg);
}

.task-content {
  padding: 0.75rem;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.task-options {
  margin-bottom: 0.75rem;
}

.finish-task-checkbox {
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.5rem;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 4px;
}

.finish-task-checkbox label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #856404;
  direction: rtl;
}

.finish-task-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #48c774;
}

.finish-task-checkbox.is-disabled {
  opacity: 0.6;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
}

.finish-task-checkbox.is-disabled label {
  cursor: not-allowed;
  color: #6c757d;
}

.finish-task-checkbox.is-disabled input[type="checkbox"] {
  cursor: not-allowed;
}

.want-different-task-checkbox {
  padding: 0.5rem 0.75rem;
  background: #d1ecf1;
  border: 1px solid #bee5eb;
  border-radius: 4px;
}

.want-different-task-checkbox label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #0c5460;
  direction: rtl;
}

.want-different-task-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #3273dc;
}

.task-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.task-button {
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #fff;
  background: #667eea;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  white-space: nowrap;
}

.task-button:hover:not(:disabled) {
  background: #5568d3;
  transform: translateY(-1px);
}

.task-button.active {
  background: #48c774;
}

.task-button.disabled,
.task-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.selected-task-info {
  margin-top: 0.75rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border-left: 3px solid #667eea;
  border-radius: 4px;
}

.selected-task-info p {
  margin: 0;
  color: #495057;
  font-size: 0.85rem;
  font-weight: 600;
}

.loading {
  margin-top: 0.75rem;
  padding: 0.5rem;
  text-align: center;
  color: #6c757d;
  font-style: italic;
  font-size: 0.85rem;
}

.message {
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  text-align: center;
  font-weight: 600;
  font-size: 0.85rem;
}

.message.success {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.message.error {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}
</style>
